import { CameraModeMask, CameraControl } from '@egr/wcf/modules/core/view/CameraControl';
import { ElementCategory, SceneElement, UndoOperation, BoundingBox } from '@egr/wcf/modules/core/mdl';
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { VisibilityMode } from '../RoomManager';
import { Util } from '../utils/Util';
import { perpendicularVector2, projectPointOnLine2ToRef, VECTOR3_ZERO, VECTOR3_Y } from '@egr/wcf/modules/core/utils/MathUtils';
import { WallAdditionInteractors } from './../tool/WallAdditionInteractors';
import { LengthProperty, PropertyValue, PropertyChangedResult, StringProperty } from '@egr/wcf/modules/core/prop';
import { clamp, eq, EPSILON } from '@egr/wcf/modules/utils/math';
import { EditWallAddition } from '../cmd/EditWallAddition';
import { newError, ERR_INVALID_STATE } from '@egr/wcf/modules/utils/error';
import { deserializeVector2, serializeVector2 } from '@egr/wcf/modules/core/io';
import { clone as arrClone } from '@egr/wcf/modules/utils/array';
import { createAdditionGeometryType } from './geo/AdditionGeometryHelper';
export class WallAddition extends SceneElement {
    constructor(pRoomManager, pType, pWidth, pHeight, pKeepRatio = true, pImageType = WallAddition.ImageType.image_3_2, pFrameWidth = 0.015, pFrameColor = '#000000', pSuspensionHeight = 1.2, pmatCategory = 'color', pmatValue = '#aaaaaa') {
        super(pRoomManager.app.model, new TransformNode("room:WallAddition_" + pType, pRoomManager.app.model.scene));
        this.mRoomManager = pRoomManager;
        this.mScene = pRoomManager.app.model.scene;
        this.mInteractors = new WallAdditionInteractors(this.mRoomManager, this);
        this.mIdStr = Util.HashString();
        this.mType = pType;
        this.mKeepRatio = pKeepRatio;
        this.mImageType = pImageType;
        this.mPosition = Vector2.Zero();
        this.mWall = null;
        this.mWallAttachData = null;
        this.mFloorOffset = 0.0;
        this.mMirrorVertical = false;
        this.mMirrorHorizontal = false;
        this.mMaterialId = "";
        this.mMaterialSet = {};
        this.mMaterialSet.category = "color";
        this.mMaterialSet.value = "#aaaaaa";
        this.mMaterialSet.loadMatId = "";
        this.mMaterialSet.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
        this.mGeometryUpdateDepth = 0;
        this.mPendingGeometryUpdate = false;
        this.mHandleTansformationChange = true;
        this.mPendingTransformMatrix = null;
        this.mPickPlane = new Plane(0, 0, 0, 0);
        //create geometry groups
        this.mNode.freezeWorldMatrix();
        this.m3dGroup = new TransformNode("group_3d", this.mScene);
        this.m3dGroup.parent = this.mNode;
        this.m3dGroup.freezeWorldMatrix();
        this.m2dGroup = this.m3dGroup.clone("group_2d", this.mNode);
        this.m2dGroup.position.y = 0.001;
        this.m2dGroup.freezeWorldMatrix();
        //create geometry instance and setup intial parameters
        this.mGeometry = createAdditionGeometryType(this.mRoomManager, this, pType);
        if (this.mGeometry == null)
            throw new Error("Failed to create AdditionGeometry: " + pType);
        let tParams = this.mGeometry.getInitialParams();
        this.mWidth = tParams.width;
        this.mHeight = tParams.height;
        this.mDepth = tParams.depth;
        this.mWallOffset = tParams.wallOffset;
        this.mColor = tParams.color;
        this.mGeometryOffset_Z = 0;
        if (this.mType === "PictureFrame") {
            if (pWidth > 0) {
                this.mWidth = pWidth;
            }
            if (pHeight > 0) {
                this.mHeight = pHeight;
            }
            if (pImageType === 3 || pImageType === 4) {
                this.mMaterialSet.value = "#ffffff";
            }
            this.mFrameWidth = pFrameWidth;
            this.mColor = pFrameColor;
            this.mMaterialSet.category = pmatCategory;
            this.mMaterialSet.value = pmatValue;
            tParams.floorOffset = pSuspensionHeight;
            this.checkMaterial(this.mMaterialSet);
        }
        else {
            this.getDefaultMaterialSet();
        }
        this.setFloorOffset(tParams.floorOffset, false);
        this.mwasCloned = false;
    }
    get type() {
        return (this.mType);
    }
    get width() {
        return (this.mWidth);
    }
    get startPoint() {
        return (this.mStartPoint);
    }
    get endPoint() {
        return (this.mEndPoint);
    }
    get height() {
        return (this.mHeight);
    }
    get depth() {
        return (this.mDepth);
    }
    get frameWidth() {
        return (this.mFrameWidth);
    }
    get ImageType() {
        return (this.mImageType);
    }
    get floorOffset() {
        return (this.mFloorOffset);
    }
    get materialSet() {
        return (this.mMaterialSet);
    }
    get materialId() {
        return (this.mMaterialId);
    }
    get PickPlane() {
        if (this.mWall == null)
            return (null);
        return (this.mWall.PickPlane);
    }
    get wasCloned() {
        return (this.mwasCloned);
    }
    get _WallAttachData() {
        return (this.mWallAttachData);
    }
    get wallOffset() {
        return (this.mWallOffset);
    }
    get WallThickness() {
        return (this.mWall != null ? this.mWall.thickness : 0.24);
    }
    get color() {
        return (this.mColor);
    }
    get mirrorVertical() {
        return (this.mMirrorVertical);
    }
    get mirrorHorizontal() {
        return (this.mMirrorHorizontal);
    }
    get wall() {
        return (this.mWall);
    }
    get geometry() {
        return (this.mGeometry);
    }
    get position() {
        return (this.mPosition);
    }
    getGeometryOffset_Z(pwoAdditionDepth = false) {
        let tRes = Vector3.Zero();
        if (this.mWall != null) {
            let tAddDepth = 0;
            if (pwoAdditionDepth) {
                tAddDepth = this.mDepth - 0.003;
                if (this.mMirrorHorizontal)
                    tAddDepth = 0.003;
            }
            if (this.mType === "Radiator") {
                let tperpDir = (perpendicularVector2(this.mWall.wallDirection)).normalize();
                tperpDir = Util.invert2d(tperpDir);
                tperpDir.scaleInPlace(this.mGeometryOffset_Z + tAddDepth);
                tRes = Util.to3d(tperpDir);
            }
            if (this.mType === "PictureFrame") {
                let tperpDir = (perpendicularVector2(this.mWall.wallDirection)).normalize();
                tperpDir = Util.invert2d(tperpDir);
                tperpDir.scaleInPlace(this.mGeometryOffset_Z + tAddDepth);
                tRes = Util.to3d(tperpDir);
            }
        }
        return (tRes);
    }
    get category() {
        return (ElementCategory.FloorPlan);
    }
    dispose() {
        super.dispose();
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            tNode.dispose();
        }
        let t2dMeshes = this.m2dGroup.getChildMeshes();
        for (let tMesh of t2dMeshes) {
            tMesh.dispose();
        }
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            tNode.dispose();
        }
        let t3dMeshes = this.m3dGroup.getChildMeshes();
        for (let tMesh of t3dMeshes) {
            tMesh.dispose();
        }
        this.mWall = null;
        this.mInteractors.dispose();
    }
    canTranslate() {
        return (this.isSelected && this.mRoomManager.app.model.selection.length > 1);
    }
    canRotate() {
        return (this.isSelected && this.mRoomManager.app.model.selection.length > 1);
    }
    showInteractors(pShow) {
        this.mInteractors.showInteractors(pShow);
    }
    clone(pNewParent) {
        let tClone = new WallAddition(this.mRoomManager, this.mGeometry.getType(), this.mWidth, this.mHeight, this.mKeepRatio);
        tClone.mwasCloned = true;
        tClone.beginGeometryUpdate();
        tClone.node.parent = pNewParent != null ? pNewParent : null;
        tClone.copyDataFrom(this);
        tClone.mColor = this.mColor;
        tClone.mMaterialId = this.mMaterialId;
        this.mRoomManager.checkMaterial(tClone.mMaterialSet).then((pResult) => {
            if (pResult === 1 || pResult === 2) {
                tClone.updateGeometry();
                tClone.notifyChange(ElementChangedFlags.PropertyValues);
                tClone.notifyChange(ElementChangedFlags.Properties);
            }
        });
        let tWall = tClone.wall;
        if (tWall == null) {
            tClone.dispose();
            return (null);
        }
        let tcheck = tWall.checkInsertWallElement(tClone, false, Util.to3d(this.position), this.position, true);
        if (!tcheck) {
            tWall._removeAddition(tClone);
            tClone.dispose();
            this.mRoomManager.eventNotEnoughWallSpaceLeft.trigger(this.mRoomManager);
            return (null);
        }
        tClone.updateGeometry();
        tClone.endGeometryUpdate();
        return (tClone);
    }
    saveUndoData(pOperation) {
        return (this._saveUndoData());
    }
    loadUndoData(pOperation, pUndoData) {
        this._loadUndoData(pUndoData);
        if (pOperation === UndoOperation.Transform)
            this.mRoomManager._removeTransformedElement(this);
    }
    changePictureFrameType(pChangeToType) {
        if (this.mType !== "PictureFrame")
            return;
        switch (pChangeToType) {
            case WallAddition.ImageType.image_3_2:
                this.mImageType = pChangeToType;
                this.mWidth = 0.6;
                this.mHeight = 0.4;
                this.mFrameWidth = 0.005;
                this.mKeepRatio = true;
                this.mMaterialSet.category = 'picture_frames_3_2';
                this.mMaterialSet.value = 'motiv_05';
                this.checkMaterial(this.mMaterialSet);
                break;
            case WallAddition.ImageType.image_2_3:
                this.mImageType = pChangeToType;
                this.mWidth = 0.4;
                this.mHeight = 0.6;
                this.mFrameWidth = 0.005;
                this.mKeepRatio = true;
                this.mMaterialSet.category = 'picture_frames_2_3';
                this.mMaterialSet.value = 'motiv_23';
                this.checkMaterial(this.mMaterialSet);
                break;
            case WallAddition.ImageType.image_1_1:
                this.mImageType = pChangeToType;
                this.mWidth = 0.6;
                this.mHeight = 0.6;
                this.mFrameWidth = 0.005;
                this.mKeepRatio = true;
                break;
            case WallAddition.ImageType.whiteboard:
                this.mImageType = pChangeToType;
                this.mWidth = 1.2;
                this.mHeight = 0.8;
                this.mFrameWidth = 0.015;
                this.mKeepRatio = false;
                this.mMaterialSet.category = 'color';
                this.mMaterialSet.value = '#ffffff';
                this.checkMaterial(this.mMaterialSet);
                break;
            case WallAddition.ImageType.customImage:
                this.mImageType = pChangeToType;
                this.mWidth = 0.6;
                this.mHeight = 0.6;
                this.mFrameWidth = 0.005;
                this.mKeepRatio = true;
                break;
            case WallAddition.ImageType.chalkboard:
                this.mImageType = pChangeToType;
                this.mWidth = 0.45;
                this.mHeight = 0.3;
                this.mFrameWidth = 0.005;
                this.mKeepRatio = false;
                this.mMaterialSet.category = 'miscellaneous';
                this.mMaterialSet.value = 'chalkboard_08';
                this.checkMaterial(this.mMaterialSet);
                break;
            default:
                this.mImageType = pChangeToType;
                this.mWidth = 0.6;
                this.mHeight = 0.4;
                this.mKeepRatio = true;
                this.getDefaultMaterialSet();
                break;
        }
        this.notifyChange(ElementChangedFlags.Properties);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.BoundingBox);
        this.updateGeometry();
    }
    getSelectionHighlightBoundingBox() {
        let tFloorOffset = this.mFloorOffset;
        let tHW = this.mWidth * 0.5;
        let tHD = this.depth * 0.5;
        let tHeight = this.mFloorOffset + this.mHeight;
        let tbbZ = this.WallThickness * 0.5 + tHD + this.wallOffset;
        if (this.mirrorHorizontal)
            tbbZ = -tbbZ;
        let tBounds = new BoundingBox(new Vector3(-tHW, 0, -tHD - tbbZ), new Vector3(tHW, tHeight, tHD - tbbZ));
        return (tBounds);
    }
    getMaterialId(pMaterial) {
        let tRes = "";
        let ttmp = pMaterial.category + this.mRoomManager.materialSplitStr + pMaterial.value;
        if (ttmp.indexOf(this.mRoomManager.materialSplitStr) === 0)
            tRes = "color" + this.mRoomManager.materialSplitStr + pMaterial.value;
        else
            tRes = ttmp;
        return (tRes);
    }
    getProperties() {
        let tPromise = super.getProperties().then((pProperties) => {
            let tProperties = pProperties;
            if (tProperties == null)
                tProperties = new Array();
            // type choices
            let tPicFrameTypeChoices = this.mRoomManager.PictureFrameTypes;
            //frame type
            // 2020-09-07: removed; git #126
            // let tPicFrameTypeProp = new StringProperty(this, WallAddition.PROP_KEY_FRAMETYPE,
            //     this.mRoomManager.resources.propertyPictureFrameType);
            // tPicFrameTypeProp.choiceList = true;
            // tPicFrameTypeProp.getChoices = () => Promise.resolve(tPicFrameTypeChoices);
            // tPicFrameTypeProp.getValue = () => tPicFrameTypeChoices[this.mImageType];
            // tPicFrameTypeProp.setValue = (pValue) => {
            //     if (pValue != null) {
            //         let tType = NumberProperty.ConvertValueToNumber(pValue)!;
            //         this.changePictureFrameType(tType);
            //         return (Promise.resolve(PropertyChangedResult.Value));
            //     }
            //         return (Promise.resolve(PropertyChangedResult.Nothing));
            // };
            // tPicFrameTypeProp.visible = this.mType === "PictureFrame";
            // tProperties.push(tPicFrameTypeProp);
            //width
            let tWidthProp = new LengthProperty(this, WallAddition.PROP_KEY_WIDTH, this.mRoomManager.resources.propertyWidth);
            tWidthProp.getValue = () => new PropertyValue(this.mWidth);
            tWidthProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setWidth(tValue))
                    return (Promise.resolve(PropertyChangedResult.Value));
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            switch (this.mImageType) {
                case WallAddition.ImageType.image_3_2:
                case WallAddition.ImageType.chalkboard:
                case WallAddition.ImageType.whiteboard:
                case WallAddition.ImageType.customImage:
                    tWidthProp.editable = true;
                    break;
                default:
                    tWidthProp.editable = false;
                    break;
            }
            tProperties.push(tWidthProp);
            //height
            let tHeightProp = new LengthProperty(this, WallAddition.PROP_KEY_HEIGHT, this.mRoomManager.resources.propertyHeight);
            tHeightProp.getValue = () => new PropertyValue(this.mHeight);
            tHeightProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setHeight(tValue))
                    return (Promise.resolve(PropertyChangedResult.Value));
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tHeightProp.editable = false;
            switch (this.mImageType) {
                case WallAddition.ImageType.image_2_3:
                case WallAddition.ImageType.image_1_1:
                case WallAddition.ImageType.chalkboard:
                case WallAddition.ImageType.whiteboard:
                    tHeightProp.editable = true;
                    break;
                default:
                    tHeightProp.editable = false;
                    break;
            }
            if (this.mType === "Radiator") {
                tHeightProp.editable = true;
            }
            tProperties.push(tHeightProp);
            //frame thickness
            if (this.mType === "PictureFrame") {
                let tframeThicknessProp = new LengthProperty(this, WallAddition.PROP_KEY_FRAMEWIDTH, this.mRoomManager.resources.propertyFrameWidth);
                tframeThicknessProp.getValue = () => new PropertyValue(this.mFrameWidth);
                tframeThicknessProp.setValue = (pValue) => {
                    let tValue = LengthProperty.ConvertValueToNumber(pValue);
                    if (tValue != null && this.setFrameWidth(tValue))
                        return (Promise.resolve(PropertyChangedResult.Value));
                    return (Promise.resolve(PropertyChangedResult.Nothing));
                };
                tProperties.push(tframeThicknessProp);
                switch (this.mImageType) {
                    case WallAddition.ImageType.whiteboard:
                        tframeThicknessProp.editable = false;
                        break;
                    default: //3
                        tframeThicknessProp.editable = true;
                        break;
                }
            }
            //floor offset
            let tFloorOffsetProp = new LengthProperty(this, WallAddition.PROP_KEY_FLOOROFFSET, this.mRoomManager.resources.propertySuspensionHeight);
            tFloorOffsetProp.getValue = () => new PropertyValue(this.mFloorOffset);
            tFloorOffsetProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.setFloorOffset(tValue, true);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tProperties.push(tFloorOffsetProp);
            //add geometry properties
            let tGeomProps = this.mGeometry.getProperties();
            if (tGeomProps != null) {
                for (let tProp of tGeomProps)
                    tProperties.push(tProp);
            }
            return (tProperties);
        });
        return (tPromise);
    }
    setColor(pColor) {
        if (this.mColor === pColor)
            return (false);
        this.mColor = pColor;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        if (this.mWall != null)
            this.mWall.updateGeometry();
        return (true);
    }
    setKeepRatio(pValue) {
        this.mKeepRatio = pValue;
    }
    setPosition(pNewPos, pNotifyWall = true) {
        if (pNewPos == null || this.mWall == null)
            return (false);
        // avoid moving opening across start/end point
        let tdir = this.mWall.wallDirection.clone();
        let tstartPoint = this.mWall.startPoint.add(tdir.scale(this.mWidth * 0.5));
        let tendPoint = this.mWall.endPoint.subtract(tdir.scale(this.mWidth * 0.5));
        projectPointOnLine2ToRef(pNewPos, tstartPoint, tendPoint, true, this.mPosition);
        this.mStartPoint = this.mPosition.subtract(tdir.scale(this.mWidth * 0.5));
        this.mEndPoint = this.mPosition.add(tdir.scale(this.mWidth * 0.5));
        this.updateWallAttachData();
        this.updateMatrix();
        if (pNotifyWall)
            this.mWall.updateGeometry();
        return (true);
    }
    setGeometryOffset_Z(pOffset) {
        this.mGeometryOffset_Z = pOffset;
    }
    set_MaterialSet(pMaterialSet) {
        this.mMaterialSet.category = pMaterialSet.category;
        this.mMaterialSet.value = pMaterialSet.value;
    }
    setWidth(pValue, pOnlySetValue = false) {
        if (this.mWall == null)
            return (false);
        let tValue = clamp(pValue, 0.01, this.mWall.getWallLengthBase());
        if (eq(this.mWidth, tValue))
            return (false);
        if (pOnlySetValue) {
            this.mWidth = pValue;
            return (true);
        }
        let tRatio = this.mWidth / this.mHeight;
        this.mWidth = tValue;
        if (this.mKeepRatio) {
            let tnewHeight = this.mWidth / tRatio;
            this.setHeight(tnewHeight, true);
            if (tnewHeight + this.mFloorOffset > this.mWall.height) {
                let tnewFloorOffset = this.mWall.height - tnewHeight;
                this.setFloorOffset(tnewFloorOffset, true);
            }
        }
        this.setPosition(this.mPosition, false);
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        return (true);
    }
    setHeight(pValue, pOnlySetValue = false) {
        if (this.mWall == null)
            return (false);
        let tValue = clamp(pValue, 0.01, 100.0);
        if (eq(this.mHeight, tValue))
            return (false);
        if (tValue > this.mWall.height)
            tValue = this.mWall.height;
        if (pOnlySetValue) {
            this.mHeight = pValue;
            return (true);
        }
        let tRatio = this.mWidth / this.mHeight;
        this.mHeight = tValue;
        if (this.mKeepRatio) {
            this.setWidth(this.mHeight * tRatio, true);
            this.setPosition(this.mPosition, false);
        }
        if (this.mHeight + this.mFloorOffset > this.mWall.height) {
            let tnewFloorOffset = this.mWall.height - this.mHeight;
            this.setFloorOffset(tnewFloorOffset, true);
        }
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        return (true);
    }
    setFrameWidth(pValue, pOnlySetValue = false) {
        let tValue = clamp(pValue, 0, 0.1);
        if (eq(this.mFrameWidth, tValue))
            return (false);
        if (pOnlySetValue) {
            this.mFrameWidth = pValue;
            return (true);
        }
        this.mFrameWidth = tValue;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        return (true);
    }
    setFloorOffset(pValue, pNotifyChange) {
        let tValue = clamp(pValue, 0.0, 100.0);
        if (this.mWall != null && (tValue + this.mHeight > this.mWall.height)) {
            tValue = this.mWall.height - this.mHeight;
        }
        this.mFloorOffset = tValue;
        this.m3dGroup.position.y = this.mFloorOffset;
        this.m2dGroup.position.y = this.mFloorOffset;
        if (this.mType == "PictureFrame" || this.mType == "Radiator") {
            this.m2dGroup.position.y = 0.001;
            //this.update2dGeometry();
        }
        if (pNotifyChange) {
            this.notifyChange(ElementChangedFlags.PropertyValues |
                ElementChangedFlags.BoundingBox);
            if (this.mWall != null)
                this.mWall.updateGeometry();
        }
    }
    setMaterialId(pId) {
        this.mMaterialId = pId;
    }
    setMaterialCategory(pValue, pMatOfCat = "", pMaterial, pforceSet = false) {
        pMaterial.category = pValue === "color" ? "" : pValue;
        // clear loaded material id
        pMaterial.loadMatId = "";
        let t1stMatOfCat = pMatOfCat;
        if (t1stMatOfCat === "") {
            t1stMatOfCat = this.mRoomManager._getFirstextMaterialOfCategory(pValue);
        }
        pMaterial.value = t1stMatOfCat;
        this.checkMaterial(pMaterial);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        return (true);
    }
    setMaterial(pValue, pMaterial) {
        let tmatValue = StringProperty.ConvertValueToString(pValue);
        if (tmatValue.trim() === "") {
            pMaterial.value = "";
            // clear loaded material id
            pMaterial.loadMatId = "";
            return (false);
        }
        // clear loaded material id
        pMaterial.loadMatId = "";
        let tmatGroup = tmatValue.split(this.mRoomManager.materialSplitStr);
        if (tmatGroup !== undefined) {
            if (tmatGroup.length === 1) {
                tmatValue = tmatGroup[0];
            }
            if (tmatGroup.length > 1) {
                tmatValue = tmatGroup[1];
            }
        }
        else {
            tmatValue = "";
        }
        pMaterial.value = tmatValue;
        this.checkMaterial(pMaterial);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (true);
    }
    attachToWall(pWall, pInsertPos) {
        if (pWall == null || pWall === this.mWall)
            return;
        if (this.mWall != null) {
            this.mWall._removeAddition(this);
            this.mWall = null;
        }
        this.mWall = pWall;
        if (pInsertPos != null)
            this.setPosition(pInsertPos, false);
        this.mWall._addAddition(this);
    }
    updateAttachedPosition(pNotifyWall = true) {
        if (this.mNode == null || this.mWall == null || this.mWallAttachData == null)
            return;
        let tWallLength = Vector2.Distance(this.mWall.startPoint, this.mWall.endPoint);
        let tWallDir = this.mWall.endPoint.subtract(this.mWall.startPoint).normalize();
        if (!this.mWall.startPoint.equalsWithEpsilon(this.mWallAttachData.start, EPSILON)) {
            if (this.mWallAttachData.distanceEnd > tWallLength)
                this.mWallAttachData.distanceEnd = tWallLength;
            tWallDir.scaleInPlace(this.mWallAttachData.distanceEnd);
            this.mPosition = this.mWall.endPoint.subtract(tWallDir);
        }
        else {
            if (this.mWallAttachData.distanceStart > tWallLength)
                this.mWallAttachData.distanceStart = tWallLength;
            tWallDir.scaleInPlace(this.mWallAttachData.distanceStart);
            this.mPosition = this.mWall.startPoint.add(tWallDir);
        }
        this.updateWallAttachData();
        this.updateMatrix();
        let tdir = this.mWall.wallDirection.clone();
        this.mStartPoint = this.mPosition.subtract(tdir.scale(this.mWidth * 0.5));
        this.mEndPoint = this.mPosition.add(tdir.scale(this.mWidth * 0.5));
        if (pNotifyWall)
            this.mWall.updateGeometry();
    }
    mirror() {
        let tCmd = new EditWallAddition(this.mRoomManager, this);
        if (this.geometry.canMirrorV && this.geometry.canMirrorH) {
            let tH = this.mirrorHorizontal;
            let tV = this.mirrorVertical;
            if (tH && tV)
                this.setMirrorVertical(false);
            else if (tH)
                this.setMirrorHorizontal(false);
            else if (tV)
                this.setMirrorHorizontal(true);
            else
                this.setMirrorVertical(true);
        }
        else if (this.geometry.canMirrorV) {
            this.setMirrorVertical(!this.mirrorVertical);
        }
        else if (this.geometry.canMirrorH) {
            this.setMirrorHorizontal(!this.mirrorHorizontal);
        }
        this.mRoomManager.app.commands.executeCommand(tCmd);
    }
    setMirrorVertical(pMirror) {
        if (pMirror === this.mMirrorVertical)
            return;
        this.mMirrorVertical = pMirror;
        this.updateGeometry();
    }
    setMirrorHorizontal(pMirror) {
        if (pMirror === this.mMirrorHorizontal)
            return;
        this.mMirrorHorizontal = pMirror;
        this.updateGeometry();
    }
    beginGeometryUpdate() {
        this.mGeometryUpdateDepth++;
    }
    endGeometryUpdate() {
        this.mGeometryUpdateDepth--;
        if (this.mGeometryUpdateDepth <= 0) {
            this.mGeometryUpdateDepth = 0;
            if (this.mPendingGeometryUpdate)
                this.updateGeometry();
        }
    }
    updateGeometry() {
        if (this.mNode == null)
            return;
        if (this.mGeometryUpdateDepth > 0) {
            this.mPendingGeometryUpdate = true;
            return;
        }
        this.mPendingGeometryUpdate = false;
        this.update2dGeometry();
        this.update3dGeometry();
        this.notifyChange(ElementChangedFlags.BoundingBox);
    }
    getCSGCutObject() {
        if (this.mWall == null)
            throw newError(ERR_INVALID_STATE, "wall is null");
        let tHW = this.mWidth * 0.5;
        let tHH = this.mHeight * 0.5;
        let tHD = (this.WallThickness + 0.002) * 0.5; //add 1 mm offset on both sides
        let tCube = CSG.cube({
            corner1: [-tHW, -tHH, -tHD],
            corner2: [tHW, tHH, tHD]
        });
        //translate
        let tPos = Vector3.TransformCoordinates(this.mNode.position, this.mWall.transformInverted);
        tPos.y = this.mHeight * 0.5 + this.mFloorOffset;
        tCube = tCube.translate([tPos.x, tPos.y, tPos.z]);
        //TODO: use material of attached wall side ?
        /*if (this.mAttachedStructure != null && this.mAttachedEdgeId != null) {
            let tIndex = this.mAttachedStructure.polygon.getPointById(this.mAttachedEdgeId);
            if (tIndex != null) {
                let tMaterial = this.mAttachedStructure.polygon.pointsData[tIndex].material;
                let tShared = new CsgUtils.PolygonShared(tMaterial);
                for (let tPoly of tCube.polygons)
                    tPoly.shared = tShared;
            }
        }*/
        return (tCube);
    }
    getEdge(pEdge, pLocalCoords) {
        let tV;
        switch (pEdge) {
            case 0:
                tV = new Vector2(-this.mWidth * 0.5, -this.WallThickness * 0.5);
                break;
            case 1:
                tV = new Vector2(this.mWidth * 0.5, -this.WallThickness * 0.5);
                break;
            case 2:
                tV = new Vector2(this.mWidth * 0.5, this.WallThickness * 0.5);
                break;
            default: //3
                tV = new Vector2(-this.mWidth * 0.5, this.WallThickness * 0.5);
                break;
        }
        if (!pLocalCoords) {
            let tV3d = Util.to3d(tV);
            Vector3.TransformCoordinatesToRef(tV3d, this.transform, tV3d);
            tV = Util.to2d(tV3d);
        }
        return (tV);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _serializeIOData(pContext) {
        let tIOAddition = {};
        tIOAddition.type = this.mGeometry.getType();
        tIOAddition.width = this.mWidth;
        tIOAddition.height = this.mHeight;
        tIOAddition.floorOffset = this.mFloorOffset;
        tIOAddition.color = this.mColor;
        tIOAddition.keepRatio = this.mKeepRatio;
        tIOAddition.imageType = this.mImageType;
        tIOAddition.frameWidth = this.mFrameWidth;
        //material
        let tmatId = this.getMaterialId(this.mMaterialSet);
        if (tmatId.indexOf(this.mRoomManager.materialSplitStr) !== -1) {
            let tMatNo = pContext.addMaterial(tmatId);
            tIOAddition.materialNo = tMatNo;
        }
        tIOAddition.materialCAT = this.mMaterialSet.category;
        tIOAddition.materialVALUE = this.mMaterialSet.value;
        if (this.mMirrorVertical)
            tIOAddition.mirrorV = this.mMirrorVertical;
        if (this.mMirrorHorizontal)
            tIOAddition.mirrorH = this.mMirrorHorizontal;
        tIOAddition.position = serializeVector2(this.mPosition);
        //save geometry data
        let tGeomData = this.mGeometry.serializeIOData();
        if (tGeomData != null)
            tIOAddition.geometryData = tGeomData;
        return (tIOAddition);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    static _load(pRoomManager, pContext, pWall, pIOAddition) {
        if (pIOAddition == null)
            return;
        let tAddition = new WallAddition(pRoomManager, pIOAddition.type, pIOAddition.width, pIOAddition.height, pIOAddition.keepRatio);
        tAddition.mWidth = pIOAddition.width;
        tAddition.mHeight = pIOAddition.height;
        tAddition.mColor = pIOAddition.color;
        if (pIOAddition.materialNo != null) {
            let tMatId = pContext.getMaterialIdByIndex(pIOAddition.materialNo);
            if (tMatId != null) {
                tAddition.mMaterialSet.loadMatId = tMatId;
            }
        }
        if (pIOAddition.materialId !== undefined) {
            tAddition.mMaterialId = pIOAddition.materialId;
        }
        if (pIOAddition.materialCAT !== undefined) {
            tAddition.mMaterialSet.category = pIOAddition.materialCAT;
        }
        if (pIOAddition.materialVALUE !== undefined) {
            tAddition.mMaterialSet.value = pIOAddition.materialVALUE;
        }
        if (pIOAddition.keepRatio !== undefined) {
            tAddition.mKeepRatio = pIOAddition.keepRatio;
        }
        if (pIOAddition.imageType !== undefined) {
            tAddition.mImageType = pIOAddition.imageType;
        }
        if (pIOAddition.frameWidth !== undefined) {
            tAddition.mFrameWidth = pIOAddition.frameWidth;
        }
        tAddition.setFloorOffset(pIOAddition.floorOffset, false);
        if (pIOAddition.mirrorV != null)
            tAddition.mMirrorVertical = pIOAddition.mirrorV;
        if (pIOAddition.mirrorH != null)
            tAddition.mMirrorHorizontal = pIOAddition.mirrorH;
        //load geometry data
        tAddition.geometry.loadIOData(pIOAddition.geometryData);
        tAddition.attachToWall(pWall, deserializeVector2(pIOAddition.position));
        tAddition.updateGeometry();
        pRoomManager.app.model.addElement(tAddition);
    }
    /** @internal */
    _saveUndoData() {
        let tData = {};
        tData.position = this.mPosition.clone();
        tData.wall = this.mWall;
        tData.mirrorHorizontal = this.mMirrorHorizontal;
        tData.mirrorVertical = this.mMirrorVertical;
        return (tData);
    }
    /** @internal */
    _loadUndoData(pData) {
        if (pData == null)
            return;
        this.beginGeometryUpdate();
        if (pData.wall != null && pData.wall !== this.mWall)
            this.attachToWall(pData.wall, pData.position);
        else
            this.setPosition(pData.position);
        this.setMirrorHorizontal(pData.mirrorHorizontal);
        this.setMirrorVertical(pData.mirrorVertical);
        this.endGeometryUpdate();
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _getDistanceEdge(pEdge) {
        if (this.mWall == null || pEdge > 3)
            return (0);
        let tV1 = Vector2.Zero();
        let tV2 = Vector2.Zero();
        let tNRes = this.getNeighbour(pEdge, pEdge === 0 || pEdge === 3); //left/right
        let tNeighbour = tNRes.opening;
        let tWall = tNRes.wall;
        let tDist = 0;
        //use other opening as neighbour
        if (tNeighbour != null && tNeighbour.mWall != null && tWall == null) {
            let tW = this.mWidth;
            if (pEdge === 0 || pEdge === 3) {
                tDist = Vector2.Distance(this.mPosition, this.mWall.startPoint) - tW / 2;
                tDist -= Vector2.Distance(tNeighbour.mPosition, tNeighbour.mWall.startPoint);
                tDist -= tNeighbour.mWidth / 2;
            }
            else {
                tDist = Vector2.Distance(tNeighbour.mPosition, tNeighbour.mWall.startPoint);
                tDist -= tNeighbour.mWidth / 2;
                tDist -= Vector2.Distance(this.mPosition, this.mWall.startPoint) + tW / 2;
            }
        }
        else {
            tV1 = this.getEdge(pEdge, false);
            if (tWall == null) {
                if (tNeighbour == null)
                    tV2 = this.mWall._edges[pEdge];
                else {
                    switch (pEdge) {
                        case 0:
                            tV2 = tNeighbour.getEdge(1, false);
                            break;
                        case 1:
                            tV2 = tNeighbour.getEdge(0, false);
                            break;
                        case 2:
                            tV2 = tNeighbour.getEdge(3, false);
                            break;
                        case 3:
                            tV2 = tNeighbour.getEdge(2, false);
                            break;
                        default:
                            tV2 = tNeighbour.getEdge(1, false);
                            break;
                    }
                }
            }
            else if (pEdge === 0 || pEdge === 2) {
                if (tWall.startCxn.wall === this.mWall)
                    tV2 = tWall._edges[3];
                else
                    tV2 = tWall._edges[1];
            }
            else if (tWall.startCxn.wall === this.mWall)
                tV2 = tWall._edges[0];
            else
                tV2 = tWall._edges[2];
            tDist = Vector2.Distance(tV1, tV2);
            //negate distance if necessary
            if (pEdge === 0 || pEdge === 3) {
                if (!Util.vectorEquals2(this.mWall.endPoint.subtract(this.mWall.startPoint).normalize(), tV1.subtract(tV2).normalize()))
                    tDist *= -1;
            }
            else if (!Util.vectorEquals2(this.mWall.startPoint.subtract(this.mWall.endPoint).normalize(), tV1.subtract(tV2).normalize()))
                tDist *= -1;
        }
        if (eq(tDist, 0.0))
            tDist = Math.abs(tDist);
        return (tDist);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _update2dVisibility() {
        if (this.mWall === null)
            return;
        let tMeshes = this.m2dGroup.getChildMeshes(false);
        for (let tMesh of tMeshes) {
            if (this.mRoomManager.viewMode === 0 ||
                (this.mRoomManager.visibilityMode === VisibilityMode.Show2d) ||
                (this.mRoomManager.visibilityMode === VisibilityMode.Show3d && this.mWall.isHidden) ||
                (this.mRoomManager.visibilityMode === VisibilityMode.Show3d && this.mRoomManager.isInteracting)) {
                if (this.mWall.keepVisible) {
                    CameraControl.SetMeshCameraModeMask(tMesh, CameraModeMask.Top | CameraModeMask.Bottom);
                }
                else {
                    CameraControl.SetMeshCameraModeMask(tMesh, null);
                }
            }
            else {
                CameraControl.SetMeshCameraModeMask(tMesh, CameraModeMask.Top | CameraModeMask.Bottom);
            }
        }
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _update3dVisibility() {
        if (this.mWall === null)
            return;
        if (this.mRoomManager.viewMode === 0 ||
            (this.mRoomManager.visibilityMode === VisibilityMode.Show2d) ||
            (this.mRoomManager.visibilityMode === VisibilityMode.Show3d && this.mWall.isHidden) ||
            (this.mRoomManager.visibilityMode === VisibilityMode.Show3d && this.mRoomManager.isInteracting)) {
            if (this.mWall.keepVisible) {
                this.m3dGroup.setEnabled(true);
            }
            else {
                this.m3dGroup.setEnabled(false);
            }
        }
        else {
            this.m3dGroup.setEnabled(true);
        }
        //update selection bounds
        this.notifyChange(ElementChangedFlags.BoundingBox);
    }
    /** @internal */
    _finishTransformation() {
        if (this.mPendingTransformMatrix == null)
            return;
        if ((this.mWall == null || !this.mRoomManager._wasElementTransformed(this.mWall)) && !this.mwasCloned) {
            let tNewPos = Util.to2d(Vector3.TransformCoordinates(VECTOR3_ZERO, this.mPendingTransformMatrix));
            let tNearest = this.mRoomManager.getNearestWall(tNewPos);
            if (tNearest != null && tNearest != this.mWall)
                this.attachToWall(tNearest, tNewPos);
            else
                this.setPosition(tNewPos, true);
        }
        if (this.mwasCloned) {
            this.mPendingTransformMatrix = null;
            let tnewPos = this.getFreePosition();
            let tNearest = this.mRoomManager.getNearestWall(tnewPos);
            if (tNearest != null && tNearest != this.mWall) {
                this.attachToWall(tNearest, tnewPos);
            }
            else {
                //this.setPosition(tnewPos, true);
            }
        }
        this.mPendingTransformMatrix = null;
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    handleImmediateChanges() {
        super.handleImmediateChanges();
        if (this.mWall != null) {
            if (this.wasFlagChanged(ElementChangedFlags.Added))
                this.mWall._addAddition(this);
            if (this.wasFlagChanged(ElementChangedFlags.Removed))
                this.mWall._removeAddition(this);
        }
    }
    handleDelayedChanges() {
        super.handleDelayedChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Transformation) && this.mHandleTansformationChange) {
            if (this.mRoomManager._transformationStarted()) {
                this.mPendingTransformMatrix = this.transform.clone();
                this.mRoomManager._addTransformedElement(this);
            }
            else
                this.updateMatrix(); // reset transform
        }
    }
    copyDataFrom(pElement) {
        super.copyDataFrom(pElement);
        if (pElement == null || !(pElement instanceof WallAddition))
            return;
        let tOther = pElement;
        this.mWidth = tOther.mWidth;
        this.mHeight = tOther.mHeight;
        this.mColor = tOther.mColor;
        this.mImageType = tOther.mImageType;
        this.mFrameWidth = tOther.mFrameWidth;
        this.mMaterialSet.category = tOther.mMaterialSet.category;
        this.mMaterialSet.loadMatId = "";
        this.mMaterialSet.rotation = tOther.mMaterialSet.rotation;
        this.mMaterialSet.value = tOther.mMaterialSet.value;
        this.setFloorOffset(tOther.mFloorOffset, false);
        let tData = tOther._saveUndoData();
        this._loadUndoData(tData);
    }
    updateWallAttachData() {
        if (this.mWall == null) {
            this.mWallAttachData = null;
            return;
        }
        this.mWallAttachData = {};
        this.mWallAttachData.start = this.mWall.startPoint.clone();
        this.mWallAttachData.end = this.mWall.endPoint.clone();
        this.mWallAttachData.distanceStart = Vector2.Distance(this.mWallAttachData.start, this.mPosition);
        this.mWallAttachData.distanceEnd = Vector2.Distance(this.mWallAttachData.end, this.mPosition);
    }
    updateMatrix() {
        if (this.mWall == null)
            return;
        let tWallAngle = this.mWall.getWallAngle();
        this.mNode.rotationQuaternion = Quaternion.RotationAxis(VECTOR3_Y, -tWallAngle);
        this.mNode.position = Util.to3d(this.mPosition);
        this.mHandleTansformationChange = false;
        this.updateTransformation();
        this.mHandleTansformationChange = true;
    }
    async checkMaterial(pMaterial) {
        await this.mRoomManager._loadExternalMaterial(this.getMaterialId(pMaterial));
        this.updateGeometry();
        return (Promise.resolve());
    }
    getDefaultMaterialSet() {
        let tType = this.mType.toString().toLocaleLowerCase();
        let tMaterialCategory = this.mRoomManager.getMaterial_Categories(tType + "_" + this.ImageType);
        let t_category = "";
        let t_matValue = "";
        if (tMaterialCategory.length > 0 ||
            this.mImageType === WallAddition.ImageType.whiteboard ||
            this.mImageType === WallAddition.ImageType.customImage) {
            // material category
            let tProp = tMaterialCategory[0];
            if (tProp.value != null) {
                t_category = tProp.value.toString();
            }
            if (this.mImageType === WallAddition.ImageType.whiteboard || this.mImageType === WallAddition.ImageType.customImage) {
                t_category = "color";
            }
            // material value
            t_matValue = this.mRoomManager._getFirstextMaterialOfCategory(t_category);
            if (t_category !== "" && t_matValue !== "") {
                this.mMaterialSet.category = t_category;
                this.mMaterialSet.value = t_matValue;
                this.checkMaterial(this.mMaterialSet);
            }
        }
    }
    getFreePosition() {
        let tRes = this.mPosition.clone();
        if (this.mWallAttachData === null || this.mWall === null)
            return (tRes);
        let twallminEndDist = this.mWall.getWallAdditionssDistanceEnd();
        let twallminStartDist = this.mWall.getWallAdditionsDistanceStart();
        let twallDirection = this.mWall.wallDirection.clone().normalize();
        let tendDistance = twallminEndDist - this.mWidth;
        let tstartDistance = twallminStartDist - this.mWidth;
        if (tendDistance > 0) {
            let tOffset = twallminEndDist - (this.mWidth * 0.5);
            tRes = this.mWallAttachData.end.subtract(twallDirection.scale(tOffset));
        }
        else if (tstartDistance > 0) {
            let tOffset = twallminStartDist - (this.mWidth * 0.5);
            tRes = this.mWallAttachData.start.add(twallDirection.scale(tOffset));
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getNeighbour(pEdge, pLeft) {
        let tRes = {
            wall: null,
            opening: null
        };
        if (this.mWall == null)
            return (tRes);
        let tWallAdditions = arrClone(this.mWall.additions);
        //remove openings that are not on the same height level
        let tLow1 = this.mFloorOffset;
        let tHigh1 = this.mFloorOffset + this.mHeight;
        let i = 0;
        while (i < tWallAdditions.length) {
            let tLow2 = tWallAdditions[i].mFloorOffset;
            let tHigh2 = tWallAdditions[i].mFloorOffset + tWallAdditions[i].mHeight;
            if (tWallAdditions[i] !== this && (tHigh2 < tLow1 || tLow2 > tHigh1)) {
                tWallAdditions.splice(i, 1); //remove
            }
            else
                ++i;
        }
        // this can only happen if this is not yet added to the opening list of mWall
        if (tWallAdditions.length === 0)
            return (tRes);
        tWallAdditions.sort(WallAddition.cmpWallElements);
        //find neighbour openings
        if (pLeft) {
            if (tWallAdditions[0] !== this) {
                for (let c1 = 0; c1 < tWallAdditions.length; ++c1) {
                    if (tWallAdditions[c1] === this) {
                        tRes.opening = tWallAdditions[c1 - 1];
                        break;
                    }
                }
            }
        }
        else if (tWallAdditions[tWallAdditions.length - 1] !== this) {
            for (let c2 = 0; c2 < tWallAdditions.length; ++c2) {
                if (tWallAdditions[c2] === this) {
                    tRes.opening = tWallAdditions[c2 + 1];
                    break;
                }
            }
        }
        //find neighbour wall
        let tWalls = new Array();
        let tTempWalls = new Array();
        if (pEdge === 0 || pEdge === 1) {
            this.mWall.getAllSideWalls(tTempWalls, tWalls, true, true);
        }
        else
            this.mWall.getAllSideWalls(tWalls, tTempWalls, true, true);
        if (pLeft)
            tWalls.reverse();
        let tVS = this.mWall.startPoint;
        let tV;
        let tDist = Vector2.Distance(this.mPosition, tVS);
        let tDist1 = 0;
        let tDist2 = 0;
        if (tRes.opening != null)
            tDist1 = Vector2.Distance(tRes.opening.mPosition, tVS);
        for (let tWall of tWalls) {
            if (tWall.startCxn.wall === this.mWall)
                tV = tWall.startPoint;
            else
                tV = tWall.endPoint;
            tDist2 = Vector2.Distance(tV, tVS);
            if (pLeft) {
                if ((tDist2 < tDist) && (tRes.opening == null || tDist1 < tDist2)) {
                    tRes.wall = tWall;
                    break;
                }
            }
            else if ((tDist2 > tDist) && (tRes.opening == null || tDist1 > tDist2)) {
                tRes.wall = tWall;
                break;
            }
        }
        return (tRes);
    }
    update2dGeometry() {
        //dispose current geometries
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes)
            tNode.dispose();
        this.createBase2dGeometry();
        this.mGeometry.create2dGeometry(this.m2dGroup);
        this._update2dVisibility();
    }
    update3dGeometry() {
        //dispose current geometries
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes)
            tNode.dispose();
        this.mGeometry.create3dGeometry(this.m3dGroup);
        this._update3dVisibility();
    }
    createBase2dGeometry() {
        //tslint (should not be empty)
    }
    static cmpWallElements(pW1, pW2) {
        if (pW1.mWall != null && pW2.mWall != null) {
            let tL1 = Vector2.Distance(pW1.mPosition, pW1.mWall.startPoint);
            let tL2 = Vector2.Distance(pW2.mPosition, pW2.mWall.startPoint);
            return (tL1 - tL2);
        }
        return (0);
    }
}
WallAddition.WALLADDITION_2D_LINE_HEIGHT = 0.001;
//// private ///////////////////////////////////////////////////////////////////////////////
WallAddition.PROP_KEY_FRAMETYPE = "room:WallAdditon:frameType";
WallAddition.PROP_KEY_WIDTH = "room:WallAddition:width";
WallAddition.PROP_KEY_HEIGHT = "room:WallAddition:height";
WallAddition.PROP_KEY_FRAMEWIDTH = "room:WallAddition:frameThickness";
WallAddition.PROP_KEY_FLOOROFFSET = "room:WallAddition:floorOffset";
//add nested types
(function (WallAddition) {
    let ImageType;
    (function (ImageType) {
        ImageType[ImageType["image_3_2"] = 0] = "image_3_2";
        ImageType[ImageType["image_2_3"] = 1] = "image_2_3";
        ImageType[ImageType["image_1_1"] = 2] = "image_1_1";
        ImageType[ImageType["whiteboard"] = 3] = "whiteboard";
        ImageType[ImageType["customImage"] = 4] = "customImage";
        ImageType[ImageType["chalkboard"] = 5] = "chalkboard";
    })(ImageType = WallAddition.ImageType || (WallAddition.ImageType = {}));
})(WallAddition || (WallAddition = {}));
